import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Users, Stack, PencilSimple, MagnifyingGlass } from "@phosphor-icons/react";
import PageShell from "@/components/layout/PageShell";
import PageHeader from "@/components/layout/PageHeader";

export default function Admin() {
  const [tab, setTab] = useState("accounts");
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [show, setShow] = useState(false);
  const [showUser, setShowUser] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ account_number: "", broker: "", account_name: "", currency: "USD", leverage: 100 });
  const [userForm, setUserForm] = useState({ email: "", password: "", name: "", role: "viewer", phone: "", mfa_enabled: false });
  const [search, setSearch] = useState("");

  const loadAll = async () => {
    try {
      const [a, u] = await Promise.all([api.get("/accounts"), api.get("/users")]);
      setAccounts(a.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to load admin data");
    }
  };
  useEffect(() => { loadAll(); }, []);

  const createAccount = async (e) => {
    e.preventDefault();
    try { await api.post("/accounts", form); toast.success("Account added"); setShow(false); setForm({ account_number: "", broker: "", account_name: "", currency: "USD", leverage: 100 }); await loadAll(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed to add account"); }
  };

  const saveUser = async (e) => {
    e.preventDefault();
    try {
      if (editUser) {
        const payload = { name: userForm.name, role: userForm.role, phone: userForm.phone, mfa_enabled: userForm.mfa_enabled };
        if (userForm.password) payload.password = userForm.password;
        await api.patch(`/users/${editUser.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", userForm);
        toast.success("User created");
      }
      setShowUser(false); setEditUser(null);
      setUserForm({ email: "", password: "", name: "", role: "viewer", phone: "", mfa_enabled: false });
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEditUser = (u) => {
    setEditUser(u);
    setUserForm({
      email: u.email,
      password: "",
      name: u.name,
      role: u.role,
      phone: u.phone || "",
      mfa_enabled: !!u.mfa_enabled,
    });
    setShowUser(true);
  };

  const toggleAcc = async (id, enabled) => {
    try {
      await api.patch(`/accounts/${id}`, { enabled: !enabled });
      await loadAll();
      toast.success(enabled ? "Account disabled" : "Account enabled");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to update account");
    }
  };

  const removeAcc = async (id) => {
    if (!window.confirm("Delete this account permanently?")) return;
    try {
      await api.delete(`/accounts/${id}`);
      await loadAll();
      toast.success("Account removed");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to delete account");
    }
  };

  const removeUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`); await loadAll(); toast.success("User removed");
  };

  const filteredAccounts = accounts.filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${a.account_number} ${a.broker} ${a.account_name}`.toLowerCase().includes(q);
  });

  const filteredUsers = users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${u.email} ${u.name} ${u.role}`.toLowerCase().includes(q);
  });

  return (
    <PageShell>
      <PageHeader eyebrow="Administration" title="ADMIN PANEL" />

      <div className="page-filter-bar mb-6">
        <div className="page-filter-field w-full sm:w-52">
          <span className="page-filter-label">Section</span>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => { setTab("accounts"); setSearch(""); }}
              data-testid="tab-accounts"
              className={`flex-1 min-h-[40px] px-3 text-xs font-mono-d uppercase tracking-widest border flex items-center justify-center gap-1.5 transition-colors ${
                tab === "accounts" ? "border-app-accent text-app-accent bg-app-accent-soft" : "border-app-border text-app-subtle hover:bg-app-hover"
              }`}
            >
              <Stack size={12} /> Accounts
            </button>
            <button
              type="button"
              onClick={() => { setTab("users"); setSearch(""); }}
              data-testid="tab-users"
              className={`flex-1 min-h-[40px] px-3 text-xs font-mono-d uppercase tracking-widest border flex items-center justify-center gap-1.5 transition-colors ${
                tab === "users" ? "border-app-accent text-app-accent bg-app-accent-soft" : "border-app-border text-app-subtle hover:bg-app-hover"
              }`}
            >
              <Users size={12} /> Users
            </button>
          </div>
        </div>
        <div className="page-filter-field flex-1 min-w-0">
          <label htmlFor="admin-search" className="page-filter-label">Search</label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-app-muted pointer-events-none" />
            <input
              id="admin-search"
              placeholder={tab === "accounts" ? "Account, broker, name…" : "Email, name, role…"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-app-bg border border-app-border pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-app-accent min-h-[40px]"
            />
          </div>
        </div>
        <div className="page-filter-field w-full sm:w-36 lg:w-40">
          <span className="page-filter-label">Showing</span>
          <div className="flex items-center min-h-[40px] px-3 border border-app-border bg-app-bg font-mono-d text-sm tabular-nums text-app-text">
            {tab === "accounts" ? filteredAccounts.length : filteredUsers.length}
          </div>
        </div>
        <div className="page-filter-field w-full sm:w-40 lg:w-44">
          <span className="page-filter-label">Action</span>
          {tab === "accounts" ? (
            <button
              type="button"
              onClick={() => setShow(true)}
              data-testid="add-account-btn"
              className="w-full min-h-[40px] px-3 bg-app-accent hover:opacity-90 text-white text-xs font-mono-d uppercase tracking-widest flex items-center justify-center gap-1.5"
            >
              <Plus size={12} weight="bold" /> Add account
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { setEditUser(null); setUserForm({ email: "", password: "", name: "", role: "viewer", phone: "", mfa_enabled: false }); setShowUser(true); }}
              data-testid="add-user-btn"
              className="w-full min-h-[40px] px-3 bg-app-accent hover:opacity-90 text-white text-xs font-mono-d uppercase tracking-widest flex items-center justify-center gap-1.5"
            >
              <Plus size={12} weight="bold" /> Add user
            </button>
          )}
        </div>
      </div>

      {tab === "accounts" && (
        <>
          <div className="mb-4 px-4 py-3 border border-[#007AFF]/30 bg-[#007AFF]/5 text-xs font-mono-d text-app-subtle leading-relaxed">
            Client accounts appear automatically when VKGFX connects with your <span className="text-app-text font-semibold">EA_API_KEY</span>.
            Manual add is optional — use it only for pre-registering an account before the EA runs.
          </div>
          <div className="lg:hidden data-card-list mb-4">
            {filteredAccounts.map(a => (
              <div key={a.id} className="data-card border border-app-border bg-app-surface">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-mono-d text-sm">#{a.account_number}
                      {a.auto_registered && (
                        <span className="ml-2 px-1.5 py-0.5 text-[9px] uppercase border border-[#007AFF]/40 text-[#007AFF]">EA</span>
                      )}
                    </div>
                    <div className="text-sm text-app-subtle truncate">{a.account_name}</div>
                    <div className="text-xs text-app-muted truncate">{a.broker}</div>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 text-[10px] font-mono-d uppercase border ${a.enabled ? "border-[#10B981]/40 text-[#10B981]" : "border-[#FF3B30]/40 text-[#FF3B30]"}`}>{a.enabled ? "Active" : "Disabled"}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="data-card-row"><span className="data-card-label">Currency</span><span className="font-mono-d">{a.currency}</span></div>
                  <div className="data-card-row"><span className="data-card-label">Leverage</span><span className="font-mono-d">1:{a.leverage}</span></div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => toggleAcc(a.id, a.enabled)} data-testid={`toggle-${a.account_number}`} className="btn-touch flex-1 px-2 py-2 text-xs border border-app-border hover:bg-app-hover">{a.enabled ? "Disable" : "Enable"}</button>
                  <button onClick={() => removeAcc(a.id)} data-testid={`del-${a.account_number}`} className="btn-touch px-3 py-2 text-xs bg-[#FF3B30] hover:bg-[#FF5C53] flex items-center justify-center gap-1"><Trash size={10} /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden lg:block border border-app-border bg-app-surface">
            <table className="w-full text-sm" data-testid="admin-accounts">
              <thead><tr className="text-left border-b border-app-border">
                {["Account", "Broker", "Name", "Currency", "Leverage", "Status", "Actions"].map(h => <th key={h} className="px-3 py-3 font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">{h}</th>)}
              </tr></thead>
              <tbody>
                {filteredAccounts.map(a => (
                  <tr key={a.id} className="border-b border-app-border/70">
                    <td className="px-3 py-2.5 font-mono-d">
                      #{a.account_number}
                      {a.auto_registered && (
                        <span className="ml-2 px-1.5 py-0.5 text-[9px] uppercase border border-[#007AFF]/40 text-[#007AFF]">EA</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">{a.broker}</td>
                    <td className="px-3 py-2.5 text-app-subtle">{a.account_name}</td>
                    <td className="px-3 py-2.5 font-mono-d">{a.currency}</td>
                    <td className="px-3 py-2.5 font-mono-d">1:{a.leverage}</td>
                    <td className="px-3 py-2.5"><span className={`px-2 py-0.5 text-[10px] font-mono-d uppercase border ${a.enabled ? "border-[#10B981]/40 text-[#10B981]" : "border-[#FF3B30]/40 text-[#FF3B30]"}`}>{a.enabled ? "Active" : "Disabled"}</span></td>
                    <td className="px-3 py-2.5 flex gap-2">
                      <button onClick={() => toggleAcc(a.id, a.enabled)} data-testid={`toggle-${a.account_number}`} className="px-2 py-1 text-xs border border-app-border hover:bg-app-hover">{a.enabled ? "Disable" : "Enable"}</button>
                      <button onClick={() => removeAcc(a.id)} data-testid={`del-${a.account_number}`} className="px-2 py-1 text-xs bg-[#FF3B30] hover:bg-[#FF5C53] flex items-center gap-1"><Trash size={10} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {show && (
            <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-4 sm:p-0">
              <form onSubmit={createAccount} className="bg-app-surface border border-app-border p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-account-modal">
                <h3 className="font-display font-bold text-xl mb-4">New Account</h3>
                <div className="space-y-3">
                  {[["account_number", "Account Number"], ["broker", "Broker"], ["account_name", "Account Name"]].map(([k, l]) => (
                    <div key={k}>
                      <label className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">{l}</label>
                      <input required value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`new-${k}`}
                        className="w-full mt-1 bg-app-bg border border-app-border px-3 py-2 text-sm" />
                    </div>
                  ))}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">Currency</label>
                      <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="w-full mt-1 bg-app-bg border border-app-border px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">Leverage</label>
                      <input type="number" value={form.leverage} onChange={(e) => setForm({ ...form, leverage: parseInt(e.target.value) || 100 })} className="w-full mt-1 bg-app-bg border border-app-border px-3 py-2 text-sm" />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col-reverse sm:flex-row gap-2 mt-5">
                  <button type="submit" data-testid="submit-account" className="btn-touch flex-1 px-4 py-2.5 bg-[#007AFF] hover:bg-[#3395FF] text-sm">Create</button>
                  <button type="button" onClick={() => setShow(false)} className="btn-touch px-4 py-2.5 border border-app-border text-sm">Cancel</button>
                </div>
              </form>
            </div>
          )}
        </>
      )}

      {tab === "users" && (
        <>
          <div className="lg:hidden data-card-list mb-4">
            {filteredUsers.map(u => (
              <div key={u.id} className="data-card border border-app-border bg-app-surface">
                <div className="font-mono-d text-sm break-all mb-1">{u.email}</div>
                <div className="text-sm mb-2">{u.name}</div>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2 py-0.5 text-[10px] font-mono-d uppercase border border-[#007AFF]/40 text-[#007AFF]">{u.role}</span>
                  {u.mfa_enabled && (
                    <span className="px-2 py-0.5 text-[10px] font-mono-d uppercase border border-[#10B981]/40 text-[#10B981]">MFA</span>
                  )}
                  <span className="font-mono-d text-[10px] text-app-muted">{u.created_at && new Date(u.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openEditUser(u)} data-testid={`edit-user-${u.email}`} className="btn-touch flex-1 px-2 py-2 text-xs border border-app-border hover:bg-app-hover flex items-center justify-center gap-1"><PencilSimple size={10} /> Edit</button>
                  <button onClick={() => removeUser(u.id)} data-testid={`del-user-${u.email}`} className="btn-touch px-3 py-2 text-xs bg-[#FF3B30] hover:bg-[#FF5C53] flex items-center justify-center"><Trash size={10} /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden lg:block border border-app-border bg-app-surface">
            <table className="w-full text-sm" data-testid="admin-users">
              <thead><tr className="text-left border-b border-app-border">
                {["Email", "Name", "Role", "Phone", "MFA", "Created", "Actions"].map(h => <th key={h} className="px-3 py-3 font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">{h}</th>)}
              </tr></thead>
              <tbody>
                {filteredUsers.map(u => (
                  <tr key={u.id} className="border-b border-app-border/70">
                    <td className="px-3 py-2.5 font-mono-d">{u.email}</td>
                    <td className="px-3 py-2.5">{u.name}</td>
                    <td className="px-3 py-2.5"><span className="px-2 py-0.5 text-[10px] font-mono-d uppercase border border-[#007AFF]/40 text-[#007AFF]">{u.role}</span></td>
                    <td className="px-3 py-2.5 font-mono-d text-xs text-app-subtle">{u.phone || "—"}</td>
                    <td className="px-3 py-2.5 font-mono-d text-xs">{u.mfa_enabled ? "On" : "—"}</td>
                    <td className="px-3 py-2.5 font-mono-d text-xs text-app-subtle">{u.created_at && new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2.5 flex gap-2">
                      <button onClick={() => openEditUser(u)} data-testid={`edit-user-${u.email}`} className="px-2 py-1 text-xs border border-app-border hover:bg-app-hover flex items-center gap-1"><PencilSimple size={10} /></button>
                      <button onClick={() => removeUser(u.id)} data-testid={`del-user-${u.email}`} className="px-2 py-1 text-xs bg-[#FF3B30] hover:bg-[#FF5C53]"><Trash size={10} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showUser && (
            <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-4 sm:p-0">
              <form onSubmit={saveUser} className="bg-app-surface border border-app-border p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" data-testid="user-modal">
                <h3 className="font-display font-bold text-xl mb-4">{editUser ? "Edit User" : "New User"}</h3>
                <div className="space-y-3">
                  {!editUser && (
                    <div>
                      <label className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">Email</label>
                      <input required type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                        className="w-full mt-1 bg-app-bg border border-app-border px-3 py-2 text-sm" />
                    </div>
                  )}
                  <div>
                    <label className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">Name</label>
                    <input required value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                      className="w-full mt-1 bg-app-bg border border-app-border px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">{editUser ? "New Password (optional)" : "Password"}</label>
                    <input type="password" required={!editUser} value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      className="w-full mt-1 bg-app-bg border border-app-border px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">Role</label>
                    <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                      className="w-full mt-1 bg-app-bg border border-app-border px-3 py-2 text-sm">
                      <option value="viewer">Viewer</option>
                      <option value="manager">Manager</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">Phone (E.164)</label>
                    <input
                      type="tel"
                      placeholder="+919876543210"
                      value={userForm.phone}
                      onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                      className="w-full mt-1 bg-app-bg border border-app-border px-3 py-2 text-sm font-mono-d"
                    />
                    <p className="mt-1 text-[10px] text-app-muted">Include country code, e.g. +91 for India</p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={userForm.mfa_enabled}
                      onChange={(e) => setUserForm({ ...userForm, mfa_enabled: e.target.checked })}
                      className="rounded border-app-border"
                    />
                    <span className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">Require SMS OTP at login</span>
                  </label>
                </div>
                <div className="flex flex-col-reverse sm:flex-row gap-2 mt-5">
                  <button type="submit" className="btn-touch flex-1 px-4 py-2.5 bg-[#007AFF] hover:bg-[#3395FF] text-sm">{editUser ? "Update" : "Create"}</button>
                  <button type="button" onClick={() => setShowUser(false)} className="btn-touch px-4 py-2.5 border border-app-border text-sm">Cancel</button>
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </PageShell>
  );
}
